import { useOutletContext } from 'react-router';

import styled from '@emotion/styled';
import { countryToLanguageCode } from '@repo/utils';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import 'keen-slider/keen-slider.min.css';

import { useCheckVerificationsComplete } from 'hooks/useCheckVerificationsComplete';
import { REVENUES_KEY, useGetRevenuesTopAllAssets } from 'services/hooks/revenue';
import { useGetMyProfile } from 'services/hooks/user';
import { useGetUsersIdentityVerifications } from 'services/hooks/verification';

import AdultToggleButton from 'components/button/AdultToggleButton';
import ButtonGroup from 'components/buttonGroup/ButtonGroup';
import Card from 'components/card/Card';
import { OutletContextDeviceType } from 'components/layout/model';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import TabList from 'components/tabList/TabList';
import AwaitQuery from 'components/util/AwaitQuery';
import Condition from 'components/util/Condition';
import EmptyView from 'components/util/EmptyView';
import Flex from 'components/util/Flex';

import { getPreviousMonthWordByLanguage } from 'pages/dashboard/utils/getMonthByLanguage';

import RankListSlider from './RankListSlider';
import { useRankContext } from '../../hooks/useRankContext';
import { AdditionalLoading } from '../shared/AdditionalLoading';

function MonthlyRevenueRankingContainer() {
  const queryClient = useQueryClient();
  const { device } = useOutletContext<OutletContextDeviceType>();
  const rowGap = device.isDesktop ? 16 : 24;
  const { sourceType } = useRankContext();
  const queryResult = useGetRevenuesTopAllAssets(sourceType);
  const { isAllVerificationCompleted, isIdentityVerificationCompleted } = useCheckVerificationsComplete();
  const { data: userProfile } = useGetMyProfile();
  const { data: identityVerificationsData } = useGetUsersIdentityVerifications();
  const isSameCountry =
    identityVerificationsData &&
    userProfile?.displayLanguageCode === countryToLanguageCode(identityVerificationsData.countryCode);

  const invalidDateQuery = () => {
    queryClient.invalidateQueries({
      queryKey: REVENUES_KEY.topAllAsset([sourceType, 'total-assets']),
    });
    queryClient.invalidateQueries({
      queryKey: REVENUES_KEY.topAllAsset([sourceType, 'my-assets']),
    });
  };

  return (
    <Card isExpanded>
      <Flex direction="column" rowGap={rowGap}>
        <Card.Header>
          <Flex direction="column" rowGap={rowGap}>
            <Flex
              isExpanded
              justify={device.isDesktop ? 'center' : 'flex-start'}
              align="center"
              style={{ position: 'relative' }}
            >
              <TabList />
              {isIdentityVerificationCompleted && isSameCountry && (
                <SAdultToggleBtnBox>
                  <AdultToggleButton handleClick={invalidDateQuery} />
                </SAdultToggleBtnBox>
              )}
            </Flex>
            <Flex columnGap={8} align="center">
              <Card.Title isExpanded>
                {t('chat.rewards_top_sales_monthly', {
                  int_month: getPreviousMonthWordByLanguage(),
                })}
              </Card.Title>
              <ButtonGroup />
            </Flex>
          </Flex>
        </Card.Header>
        <SRankingCardBody>
          <Condition condition={isAllVerificationCompleted} fallback={<EmptyView text="No more data" />}>
            <AwaitQuery
              queryResult={{
                ...queryResult,
                error: queryResult.error.entire || queryResult.error.my || null,
              }}
              initialLoadingComponent={() => <AdditionalLoading height={'250px'} />}
              additionalLoadingComponent={() => <AdditionalLoading height={'250px'} />}
            >
              <RankListSlider data={queryResult.data} />
            </AwaitQuery>
          </Condition>
        </SRankingCardBody>
      </Flex>
    </Card>
  );
}

export default withErrorBoundary(MonthlyRevenueRankingContainer);

const SRankingCardBody = styled.div`
  position: relative;
  min-height: 250px;
`;

const SAdultToggleBtnBox = styled.div`
  position: absolute;
  right: 0;
`;
